import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import '../../scss/styles.scss';
import greenroomIcon from '../../images/brandlive-green-icon.svg';
import { GreenroomState as GS } from '../../stores/types/types';
import { ConnectedUser } from '../../stores/types/connection';
import { handleClickJacking } from '../../utils/utils';
import useRoomHealth, { overallStatus } from '../../hooks/health/use-room-health';
import useResolvedRoom, { ResolveState } from '../../hooks/health/use-resolved-room';
import useMeetingConfig from '../../hooks/health/use-meeting-config';
import useProviderStatus from '../../hooks/health/use-provider-status';
import useServiceHealth from '../../hooks/health/use-service-health';
import useGroundcoverLogs, { DEFAULT_LOG_WINDOW_MS } from '../../hooks/health/use-groundcover-logs';
import useSessionMonitor from '../../hooks/health/use-session-monitor';
import useProducerAccess from '../../hooks/health/use-producer-access';
import useRememberedUsers from '../../hooks/health/use-remembered-users';
import SessionStreamsPanel from './panels/session-streams-panel';
import MeetingConfigPanel from './panels/meeting-config-panel';
import ProviderStatusPanel from './panels/provider-status-panel';
import ServiceHealthPanel from './panels/service-health-panel';
import LogsPanel from './panels/logs-panel';
import SessionEventsPanel from './panels/session-events-panel';
import HealthTabs, { HealthTab } from './health-tabs';
import { isProviderDegraded } from './provider-status';
import { AccessState, accessState, isInternalEmail } from './access';
import {
	DEFAULT_POLL_INTERVAL_MS,
	POLL_INTERVALS_MS,
	ProbeStatus,
	worstStatus,
} from './types';
import StatusPill from './status-pill';
import OpentokPanel from './panels/opentok-panel';
import BroadcastPanel from './panels/broadcast-panel';
import RoomSessionPanel from './panels/room-session-panel';
import ConnectedClientsPanel from './panels/connected-clients-panel';
import { formatAgo } from './format';

const intervalLabel = (ms: number) => (ms ? `${ms / 1000}s` : 'paused');

/** Stable identity, so an empty roster does not rebuild what remembers it. */
const EMPTY_USERS: ConnectedUser[] = [];

/** Tab order, and the only values `?tab=` accepts. */
const TABS = ['room', 'opentok', 'broadcast', 'platform', 'logs', 'config'];

/**
 * Internal, read-only view of one meeting's services. Rendered outside the
 * meeting app on `/health/:meetingSlug`, so it never touches the OpenTok or
 * LiveKit clients and cannot disturb a live show. Everything it shows comes
 * from server side state, which also means it is safe to open on a room this
 * producer is not joined to.
 *
 * The url takes the same slug producers already have in `/producer/<slug>`, so
 * a producer can swap the path segment on a room they are working in.
 */
export default function HealthDashboard() {
	const { meetingSlug } = useParams<{ meetingSlug: string }>();
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const email = useSelector((s: GS) => s.SessionReducer.user?.email);

	const [intervalMs, setIntervalMs] = useState<number>(DEFAULT_POLL_INTERVAL_MS);

	const [active, setActive] = useState(TABS[0]);

	// One-shot and on-demand sources fetch the first time their tab is opened and
	// then keep what they have, so returning to a tab is free. Polling sources
	// below key off `active` instead, so they stop when you leave.
	const [visited, setVisited] = useState<Set<string>>(() => new Set([active]));
	useEffect(() => {
		setVisited((previous) => (previous.has(active) ? previous : new Set(previous).add(active)));
	}, [active]);

	// Drives the "x ago" label between polls without re-running any probes.
	const [, setTick] = useState(0);

	// The room's channel is half of the access rule, so the lookup is gated on the
	// half that can be judged without it. The token authorises it either way.
	const mayLookUpRoom = !!token && isInternalEmail(email);
	const resolved = useResolvedRoom(mayLookUpRoom ? meetingSlug : undefined, token ?? null);
	const producer = useProducerAccess(mayLookUpRoom);

	const access = accessState({
		token,
		email,
		isProducer: producer.isProducer,
		channelIds: producer.channelIds,
		channel: resolved.state === ResolveState.Resolved
			? resolved.meeting?.channel ?? null
			: undefined,
	});
	const granted = access === AccessState.Granted;

	// Two ids: the lookup's own result drives the guards below, and the gated one
	// drives the probes. Folded together, a refusal looked like a failed lookup.
	const resolvedRoomId = resolved.meeting?.roomId ?? null;
	const roomId = granted ? resolvedRoomId : null;

	// Flags are set before a show and do not change during one, so this fetches
	// once on resolve rather than joining the poll loop.
	const meetingConfig = useMeetingConfig(visited.has('config') ? roomId : null);
	// Third party status pages, on their own cadence and independent of the room.
	const providerStatus = useProviderStatus(active === 'platform');
	// Process-level liveness for our own services, on the room's poll interval.
	const serviceHealth = useServiceHealth(intervalMs, active === 'platform');
	// This room's browser logs, fetched on demand rather than on the room's poll.
	const [logWindowMs, setLogWindowMs] = useState(DEFAULT_LOG_WINDOW_MS);
	const groundcoverLogs = useGroundcoverLogs(
		granted && visited.has('logs') ? token : null,
		granted ? meetingSlug ?? null : null,
		logWindowMs,
	);

	const { health, isRefreshing, refresh } = useRoomHealth({
		roomId,
		token: token ?? null,
		intervalMs,
	});

	// A room that is closing or closed has nothing left to watch, and a monitor
	// left connected to one is a live participant in an empty session - still
	// reporting to Vonage, still counted, for a show that is over.
	const roomSessionStatus = health?.roomSession.data?.status;
	const roomIsLive = roomSessionStatus === 'starting' || roomSessionStatus === 'started';

	// The only source of OpenTok quality now: the media server's own events, plus
	// what its subscriptions can measure off the forwarded media.
	//
	// Connects on the first visit and then holds. Both the connection and the
	// subscriptions behind it survive tab changes: a reconnect is visible to every
	// other client and restarts the timeline, and resubscribing churns media for
	// every publisher in the room. The panel's own toggle stops the measuring.
	const sessionMonitor = useSessionMonitor(
		health?.opentok.data?.sessionId ?? null,
		roomIsLive && visited.has('opentok'),
	);

	// The roster answers with who is connected now, which is right for counting
	// the room and wrong for reading its history - the session panels look back
	// over people who have since left.
	const remembered = useRememberedUsers(health?.connectedUsers.data ?? EMPTY_USERS);

	// Picking a publisher scopes the timeline below it to that stream. Held here
	// so both panels agree, and cleared when the tab changes.
	const [selectedStream, setSelectedStream] = useState<string | null>(null);
	useEffect(() => { setSelectedStream(null); }, [active]);

	// index.html ships `html { display: none }` and every entry point has to earn
	// its way out of that via the clickjacking guard. Standalone routes are not
	// covered by App's call, so this route makes its own - it both reveals the
	// page and keeps the frame-busting protection.
	useEffect(() => {
		handleClickJacking();
	}, []);

	useEffect(() => {
		const timer = setInterval(() => setTick((count) => count + 1), 1000);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		document.title = meetingSlug ? `Health · ${meetingSlug}` : 'Health';
	}, [meetingSlug]);

	if (access === AccessState.NotSignedIn) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>Signing in…</p>
			</div>
		);
	}

	// Names which rule was not met: both are things the reader can check about
	// themselves, and "no access" alone makes every one a support question.
	if (access === AccessState.NotInternal) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>This view is internal to Brandlive.</p>
				<p className='health-subtle'>
					Signed in as <span className='health-mono'>{email || 'an unknown account'}</span>.
				</p>
			</div>
		);
	}

	if (resolved.state === ResolveState.Pending) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>Looking up <span className='health-mono'>{meetingSlug}</span>…</p>
			</div>
		);
	}

	if (resolved.state !== ResolveState.Resolved || !resolvedRoomId) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>
					{resolved.state === ResolveState.Forbidden
						&& 'You do not have access to this room.'}
					{resolved.state === ResolveState.NotFound
						&& 'No room matches this url.'}
					{resolved.state === ResolveState.Failed
						&& `Could not look up this room: ${resolved.error}`}
				</p>
				<p className='health-subtle'>
					Use the slug from the producer url, for example
					<span className='health-mono'> /health/{meetingSlug || 'abc123'}</span>.
				</p>
			</div>
		);
	}

	if (access === AccessState.NotChannelProducer) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>You are not a producer on this room&apos;s channel.</p>
				<p className='health-subtle'>
					This view is per room, so producing elsewhere does not carry over.
				</p>
			</div>
		);
	}

	// The profile is still arriving; the room itself is already known.
	if (!granted) {
		return (
			<div className='health-dashboard health-dashboard--message'>
				<h1>Meeting health</h1>
				<p>Checking access to <span className='health-mono'>{meetingSlug}</span>…</p>
			</div>
		);
	}

	const overall = overallStatus(health);
	const providerDegraded = providerStatus.providers.filter(isProviderDegraded).length;

	// Platform reports nothing until its tab has been opened, so its pill has to
	// stay blank rather than read healthy.
	const platformChecked = !!serviceHealth.services.length;

	// One tab per subsystem: whatever is broken, a producer opens exactly one.
	const tabs: HealthTab[] = [
		{
			key: 'room',
			label: 'Room',
			status: worstStatus([health?.roomSession.status, health?.connectedUsers.status]),
		},
		{
			key: 'opentok',
			label: 'OpenTok',
			status: worstStatus([health?.opentok.status]),
		},
		{
			key: 'broadcast',
			label: 'Broadcast',
			status: worstStatus([health?.broadcast.status]),
		},
		{
			key: 'platform',
			label: 'Platform',
			status: platformChecked
				? worstStatus([
					providerDegraded ? ProbeStatus.Degraded : ProbeStatus.Ok,
					...serviceHealth.services.map((service) => service.probe.status),
				])
				: undefined,
		},
		{
			key: 'logs',
			label: 'Logs',
		},
		{
			key: 'config',
			label: 'Configuration',
		},
	];

	return (
		<div className='health-dashboard'>
			<header className='health-dashboard__header'>
				<div className='health-dashboard__identity'>
					<h1>
						<img className='health-dashboard__logo' src={greenroomIcon} alt='' />
						{resolved.meeting?.standaloneTitle || resolved.meeting?.slug}
						<StatusPill status={overall} large />
					</h1>
					<p className='health-subtle'>
						<span className='health-mono'>{resolved.meeting?.slug}</span>
						{' · room '}
						<span className='health-mono'>{roomId}</span>
						{' · '}
						{health
							? `updated ${formatAgo(health.fetchedAt)}`
							: 'waiting for first response'}
						{isRefreshing && ' · refreshing'}
					</p>
				</div>

				<div className='health-dashboard__controls'>
					<label htmlFor='health-interval'>Refresh</label>
					<select
						id='health-interval'
						value={intervalMs}
						onChange={(event) => setIntervalMs(Number(event.target.value))}
					>
						{POLL_INTERVALS_MS.map((ms) => (
							<option key={ms} value={ms}>{intervalLabel(ms)}</option>
						))}
					</select>
					<button
						type='button'
						onClick={refresh}
						disabled={isRefreshing}
					>
						Refresh now
					</button>
				</div>
			</header>

			{overall === ProbeStatus.Error && (
				<p className='health-banner health-banner--error'>
					One or more services did not answer. Check the panel headers for the failing probe.
				</p>
			)}

			<HealthTabs tabs={tabs} active={active} onSelect={setActive} />

			{/*
				Only the selected tab is mounted. The probes behind the others keep
				running regardless, so an unselected tab still carries its status.
			*/}
			<div className='health-dashboard__content' id='health-tab-panel' role='tabpanel'>
				{active === 'room' && (
					<div className='health-dashboard__grid'>
						<RoomSessionPanel
							probe={health?.roomSession}
							health={health}
							eventUuid={resolved.meeting?.eventUuid}
						/>
						<ConnectedClientsPanel probe={health?.connectedUsers} />
					</div>
				)}

				{active === 'opentok' && (
					<>
						<div className='health-dashboard__grid'>
							<OpentokPanel
								probe={health?.opentok}
								facts={sessionMonitor.sessionFacts}
							/>
						</div>

					<SessionStreamsPanel
						users={remembered.users}
						present={remembered.present}
						monitor={sessionMonitor}
						selected={selectedStream}
						onSelect={setSelectedStream}
					/>

					<SessionEventsPanel
						monitor={sessionMonitor}
						users={remembered.users}
						selected={selectedStream}
						onSelect={setSelectedStream}
					/>
					</>
				)}

				{active === 'broadcast' && (
					<div className='health-dashboard__grid'>
						<BroadcastPanel probe={health?.broadcast} />
					</div>
				)}

				{active === 'platform' && (
					<>
						<ServiceHealthPanel health={serviceHealth} />
						<ProviderStatusPanel status={providerStatus} />
					</>
				)}

				{active === 'logs' && (
					<LogsPanel
						logs={groundcoverLogs}
						windowMs={logWindowMs}
						onWindowChange={setLogWindowMs}
					/>
				)}

				{active === 'config' && (
					<div className='health-dashboard__grid'>
						<MeetingConfigPanel meeting={resolved.meeting} config={meetingConfig} />
					</div>
				)}
			</div>
		</div>
	);
}
